import { useEffect, useMemo, useState } from 'react';

import {
  countActiveBulletinFilters,
  DEFAULT_BULLETIN_FILTERS,
  useBulletinSearchFilters,
} from 'hooks/useBulletinSearchFilters';
import { useIsMobileLayout } from 'hooks/useMediaQuery';
import { PriceRangeInput } from '../../../components/formsV2/fields/PriceAmountField';

import { MultiSelectCheckboxField } from '../../../components/formsV2/fields/select/MultiSelectCheckboxField';
import { DEFAULT_SUBJECT } from '../../../modules/Details/NewTask/taskForm.store';
import { useCategories } from '../../../hooks/useCategories';
import styles from './TeacherSearch.module.scss';

import { clsx } from 'clsx';
import { Formik, useFormikContext } from 'formik';


import { useTutorTrustScore } from 'hooks/useTutorTrustScore';
import { useLiveCountdown } from 'hooks/useLiveCountdown';
import { formatDailyResponsesResetLabel } from 'utils/tutorDailyResponses';

export interface FilterFormValues {
  priceFrom: string;
  priceTo: string;
  subjectSubcategories: string[];
  learnerAudience: string[];
  grade: string[];
  studyLocations: string[];
}



 
const GRADE_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1} класс`,
}));

const LOCATION_OPTIONS = [
  { value: 'at_tutor', label: 'У преподавателя' },
  { value: 'at_student', label: 'У ученика' },
  { value: 'online', label: 'Онлайн' },
  { value: 'any', label: 'Любое' },
];

const LEARNER_AUDIENCE_OPTIONS = [
  { value: 'school', label: 'Школьник' },
  { value: 'graduated', label: 'Студент' },
];

export const TeacherSearchFilters = () => {
  const { values, setFieldValue, resetForm } = useFormikContext<FilterFormValues>();
  const { clear } = useBulletinSearchFilters();
  const { data: categories } = useCategories();
  const isMobile = useIsMobileLayout();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: trustScore } = useTutorTrustScore();
  const dailyResponses = trustScore?.daily_responses;
  const quotaCountdown = useLiveCountdown(dailyResponses?.resets_at);


  const filtersExpanded = !isMobile || mobileOpen;
  const activeFilterCount = countActiveBulletinFilters(values);

  const subjectOptions = useMemo(() => {
    if (!categories) return [];
    const category = categories.find((cat) => cat.name === DEFAULT_SUBJECT);
    if (!category?.subcategories) return [];
    return category.subcategories.map((sub) => ({
      value: sub.name,
      label: (sub as { title?: string }).title || sub.name,
    }));
  }, [categories]);

  const showGradeFilter =
    values.learnerAudience.length === 0 || values.learnerAudience.includes('school');

  useEffect(() => {
    const allowedAudience = values.learnerAudience.filter(
      (value) => value === 'school' || value === 'graduated',
    );
    if (allowedAudience.length !== values.learnerAudience.length) {
      setFieldValue('learnerAudience', allowedAudience);
    }
  }, [values.learnerAudience, setFieldValue]);

  useEffect(() => {
    if (!subjectOptions.length) return;
    const validSubjects = new Set(subjectOptions.map((option) => option.value));
    const filteredSubjects = values.subjectSubcategories.filter((value) =>
      validSubjects.has(value),
    );
    if (filteredSubjects.length !== values.subjectSubcategories.length) {
      setFieldValue('subjectSubcategories', filteredSubjects);
    }
  }, [subjectOptions, values.subjectSubcategories, setFieldValue]);

  useEffect(() => {
    if (!showGradeFilter && values.grade.length > 0) {
      setFieldValue('grade', []);
    }
  }, [showGradeFilter, values.grade.length, setFieldValue]);

  const handleReset = () => {
    clear();
    resetForm({ values: { ...DEFAULT_BULLETIN_FILTERS } });
    setMobileOpen(false);
  };

  return (
    <section
      className={clsx(styles.filters, filtersExpanded && styles.filtersOpen)}
      aria-label='Фильтры заявок'
    >
      <button
        type='button'
        className={styles.filtersToggle}
        onClick={() => setMobileOpen((open) => !open)}
        aria-expanded={filtersExpanded}
        aria-controls='teacher-search-filters-panel'
      >
        <span className={styles.filtersTitle}>Подберите заявки по вашим условиям</span>
        {activeFilterCount > 0 ? <span className={styles.muted}>{activeFilterCount}</span> : null}
      </button>

      <h2 className={clsx(styles.filtersTitle, styles.filtersTitleDesktop)}>
        Подберите заявки по вашим условиям
      </h2>

      <div id='teacher-search-filters-panel' className={styles.filtersBody}>
        {dailyResponses ? (
          <p className={styles.quota}>
            Откликов сегодня: {dailyResponses.responses_used} из {dailyResponses.responses_max}.
            Сброс в {formatDailyResponsesResetLabel()}
            {quotaCountdown ? ` (через ${quotaCountdown})` : ''}
          </p>
        ) : null}

        <div className={styles.filterGrid}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Направление</span>
            <MultiSelectCheckboxField
              name='subjectSubcategories'
              label=''
              options={subjectOptions}
              placeholder='Все темы'
            />
          </div>

          {showGradeFilter ? (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Класс</span>
              <MultiSelectCheckboxField
                name='grade'
                label=''
                options={GRADE_OPTIONS}
                placeholder='5-11 класс'
              />
            </div>
          ) : null}

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Бюджет за час</span>
            <PriceRangeInput
              fromValue={values.priceFrom}
              toValue={values.priceTo}
              onFromChange={(val) => setFieldValue('priceFrom', val)}
              onToChange={(val) => setFieldValue('priceTo', val)}
              fromPlaceholder='От, руб.'
              toPlaceholder='До, руб.'
              className={styles.priceRange}
              style={{ gridTemplateColumns: '1fr 1fr', gap: '8px' }}
            />
          </div>

          <div className={clsx(styles.field, styles.audienceField)}>
            <span className={styles.fieldLabel}>Кого учить</span>
            <MultiSelectCheckboxField
              name='learnerAudience'
              label=''
              options={LEARNER_AUDIENCE_OPTIONS}
              placeholder='Все'
            />
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Место занятий</span>
            <MultiSelectCheckboxField
              name='studyLocations'
              label=''
              options={LOCATION_OPTIONS}
              placeholder='Любое'
            />
          </div>
        </div>

        <div className={styles.filterActions}>
          <button type='button' className={styles.primarySm} onClick={() => setMobileOpen(false)}>
            Применить фильтры
          </button>
          <button type='button' className={styles.textBtn} onClick={handleReset}>
            Сбросить
          </button>
        </div>
      </div>
    </section>
  );
};

/* Страницы-подсказки /teacher/search/error-* рендерят фильтры без родительского Formik;
   без обертки useFormikContext падает и React Router показывает экран ошибки. */
export const StandaloneTeacherSearchFilters = () => {
  const { load } = useBulletinSearchFilters();
  const initialValues = useMemo(() => load(), [load]);
  return (
    <Formik initialValues={initialValues} enableReinitialize={false} onSubmit={() => {}}>
      <TeacherSearchFilters />
    </Formik>
  );
};
